import { c as _c } from "react/compiler-runtime";
import type { IconType } from '@lobehub/icons';
import { Anthropic, Aws, Azure, Bedrock, ChatGLM, Claude, Cline, Cohere, Cursor, DeepSeek, Gemini, GithubCopilot, Google, Grok, Groq, HuggingFace, Kimi, Meta, Minimax, Mistral, modelMappings, Nvidia, OpenAI, Perplexity, providerMappings, Qwen, Stability, Windsurf, XAI, ZAI } from '@lobehub/icons';
import { Bot } from 'lucide-react';
import { FactoryDroidIcon } from './factory-droid-icon';
import { OpenCodeIcon } from './opencode-icon';
export { Anthropic, Aws, Azure, Bedrock, ChatGLM, Claude, Cline, Cohere, Cursor, DeepSeek, FactoryDroidIcon, Gemini, GithubCopilot, Google, Grok, Groq, HuggingFace, Kimi, Meta, Minimax, Mistral, Nvidia, OpenAI, OpenCodeIcon, Perplexity, Qwen, Stability, Windsurf, XAI, ZAI };
export function UnknownAIIcon(t0) {
  const $ = _c(3);
  const {
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "1em" : t1;
  let t2;
  if ($[0] !== className || $[1] !== size) {
    t2 = <Bot width={size} height={size} className={className} />;
    $[0] = className;
    $[1] = size;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
type LobeModelIcon = (typeof modelMappings)[number]['Icon'];
type LobeProviderIcon = (typeof providerMappings)[number]['Icon'];
type AIIconComponent = IconType | LobeModelIcon | LobeProviderIcon | typeof UnknownAIIcon | typeof OpenCodeIcon | typeof FactoryDroidIcon;
const lobeModelMatchers = modelMappings.map(mapping => {
  return {
    Icon: mapping.Icon,
    patterns: mapping.keywords.map(keyword => new RegExp(keyword, 'i'))
  };
});
const toolIconMap: Record<string, AIIconComponent> = {
  'cursor': Cursor,
  'copilot': GithubCopilot,
  'github-copilot': GithubCopilot,
  'github copilot': GithubCopilot,
  'claude': Claude,
  'claude-code': Claude,
  'claude code': Claude,
  'codex': OpenAI,
  'openai-codex': OpenAI,
  'openai codex': OpenAI,
  'chatgpt': OpenAI,
  'windsurf': Windsurf,
  'codeium': Cursor,
  'aider': Claude,
  'continue': OpenAI,
  'continue.dev': OpenAI,
  'cline': Cline,
  'moonshot': Kimi,
  'kimi': Kimi,
  'kiro': Aws,
  'amazon-kiro': Aws,
  'amazon kiro': Aws,
  'opencode': OpenCodeIcon,
  'open code': OpenCodeIcon,
  'open-code': OpenCodeIcon,
  'factory-droid': FactoryDroidIcon,
  'factory droid': FactoryDroidIcon,
  'droid': FactoryDroidIcon
};
const providerIconMap: Record<string, IconType> = {
  'anthropic': Anthropic,
  'openai': OpenAI,
  'google': Gemini,
  'gemini': Gemini,
  'meta': Meta,
  'meta-llama': Meta,
  'llama': Meta,
  'mistral': Mistral,
  'mistralai': Mistral,
  'cohere': Cohere,
  'deepseek': DeepSeek,
  'nvidia': Nvidia,
  'nvidia-nim': Nvidia,
  'xai': Grok,
  'x-ai': Grok,
  'grok': Grok,
  'minimax': Minimax,
  'chatglm': ChatGLM,
  'glm': ChatGLM,
  'glmv': ChatGLM,
  'zhipu': ChatGLM,
  'qwen': Qwen,
  'qwq': Qwen,
  'alibaba': Qwen,
  'moonshot': Kimi,
  'kimi': Kimi,
  'stability': Stability,
  'stability-ai': Stability,
  'aws': Bedrock,
  'bedrock': Bedrock,
  'amazon': Bedrock,
  'azure': Azure,
  'microsoft': Azure,
  'perplexity': Perplexity,
  'huggingface': HuggingFace,
  'hugging-face': HuggingFace,
  'groq': Groq,
  'claude': Claude
};
const heuristicIconMatchers: Array<{
  pattern: RegExp;
  icon: IconType;
}> = [{
  pattern: /\bcomposer(?:[\s._/-]|$)/,
  icon: XAI
}, {
  pattern: /(?:^o\d\b|openai|gpt|codex)/,
  icon: OpenAI
}, {
  pattern: /claude/,
  icon: Claude
}, {
  pattern: /(?:anthropic|opus|sonnet|haiku)/,
  icon: Anthropic
}, {
  pattern: /(?:moonshot|kimi)/,
  icon: Kimi
}, {
  pattern: /(?:gemini|google)/,
  icon: Gemini
}, {
  pattern: /deepseek/,
  icon: DeepSeek
}, {
  pattern: /(?:qwen|qwq|alibaba)/,
  icon: Qwen
}, {
  pattern: /(?:chatglm|glmv|zhipu|glm[-_])/,
  icon: ChatGLM
}, {
  pattern: /minimax/,
  icon: Minimax
}, {
  pattern: /mistral/,
  icon: Mistral
}, {
  pattern: /(?:grok|x-ai|xai)/,
  icon: Grok
}, {
  pattern: /cohere/,
  icon: Cohere
}];
function getIconFromLobeModelMappings(identifier: string): LobeModelIcon | null {
  for (const mapping of lobeModelMatchers) {
    if (mapping.patterns.some(pattern => pattern.test(identifier))) {
      return mapping.Icon;
    }
  }
  return null;
}
function getIconFromLobeProviderKeyword(provider: string): LobeProviderIcon | null {
  for (const mapping of providerMappings) {
    if (mapping.keywords.some(keyword => keyword.toLowerCase() === provider)) {
      return mapping.Icon;
    }
  }
  return null;
}
function getIconFromLobeProviderMappings(identifier: string): LobeProviderIcon | null {
  const directMatch = getIconFromLobeProviderKeyword(identifier);
  if (directMatch) {
    return directMatch;
  }
  const slashIndex = identifier.indexOf('/');
  if (slashIndex > 0) {
    return getIconFromLobeProviderKeyword(identifier.substring(0, slashIndex));
  }
  return null;
}
function getIconFromProviderMap(identifier: string): IconType | null {
  if (providerIconMap[identifier]) {
    return providerIconMap[identifier] ?? null;
  }
  for (const [key, icon] of Object.entries(providerIconMap)) {
    if (identifier.startsWith(key)) {
      return icon;
    }
  }
  const slashIndex = identifier.indexOf('/');
  if (slashIndex > 0) {
    const provider = identifier.substring(0, slashIndex);
    if (providerIconMap[provider]) {
      return providerIconMap[provider] ?? null;
    }
  }
  for (const [key, icon] of Object.entries(providerIconMap)) {
    if (identifier.includes(key)) {
      return icon;
    }
  }
  return null;
}
function getIconFromHeuristics(identifier: string): IconType | null {
  for (const {
    pattern,
    icon
  } of heuristicIconMatchers) {
    if (pattern.test(identifier)) {
      return icon;
    }
  }
  return null;
}
function getIconFromIdentifier(identifier: string): AIIconComponent | null {
  return getIconFromLobeModelMappings(identifier) ?? getIconFromLobeProviderMappings(identifier) ?? getIconFromProviderMap(identifier) ?? getIconFromHeuristics(identifier);
}
export function getToolIcon(tool: string): AIIconComponent {
  const normalizedTool = tool.toLowerCase().trim();
  return toolIconMap[normalizedTool] || getIconFromIdentifier(normalizedTool) || UnknownAIIcon;
}
export function getModelIcon(model: string): AIIconComponent {
  const normalizedModel = model.toLowerCase().trim();
  return getIconFromIdentifier(normalizedModel) || UnknownAIIcon;
}
export function getAIIcon(name: string, type: 'tool' | 'model' = 'model'): AIIconComponent {
  return type === 'tool' ? getToolIcon(name) : getModelIcon(name);
}